import { errorMessage } from './errors';
import type { WhipLogger } from './logger';

/** The one RTP header extension this stack negotiates. Carries the capture wall-clock, which the transcoder uses to align tracks. */
export const ABS_CAPTURE_TIME_URI = 'http://www.webrtc.org/experiments/rtp-hdrext/abs-capture-time';

/**
 * The header-extension negotiation API, which TypeScript's DOM lib does not describe yet.
 *
 * Declared as its own optional shape rather than by augmenting `RTCRtpTransceiver`, because it genuinely may not be there:
 * typing it as always-present would hide exactly the check that has to happen at runtime.
 */
export interface HeaderExtensionNegotiation {
	getHeaderExtensionsToNegotiate(): { uri: string; direction: RTCRtpTransceiverDirection }[];
	setHeaderExtensionsToNegotiate(extensions: { uri: string; direction: RTCRtpTransceiverDirection }[]): void;
}

/**
 * Turns on `abs-capture-time` for one transceiver, returning whether it took.
 *
 * The API is recent enough to be missing, and a browser that has it may still not offer this extension, so every failure
 * here is reported rather than thrown: the session is perfectly viable without it.
 */
export function enableAbsCaptureTime(transceiver: RTCRtpTransceiver & Partial<HeaderExtensionNegotiation>, logger: WhipLogger): boolean {
	if (typeof transceiver.getHeaderExtensionsToNegotiate !== 'function' || typeof transceiver.setHeaderExtensionsToNegotiate !== 'function') {
		return false;
	}

	try {
		const extensions = transceiver.getHeaderExtensionsToNegotiate();
		const target = extensions.find((extension) => extension.uri === ABS_CAPTURE_TIME_URI);
		if (!target) return false;
		if (target.direction === 'sendrecv') return true;

		target.direction = 'sendrecv';
		transceiver.setHeaderExtensionsToNegotiate(extensions);
		return true;
	} catch (error) {
		logger.warn(`[whip] abs-capture-time not negotiated: ${errorMessage(error)}`);
		return false;
	}
}

/**
 * The `m=video` section of an answer, line by line, for logging.
 *
 * Worth looking at: it names the single codec the transcoder settled on, which is the one thing about the session that
 * cannot be renegotiated.
 */
export function videoSectionLines(sdp: string): string[] {
	const section = sdp.split(/\r?\n(?=m=)/).find((part) => part.startsWith('m=video'));
	if (!section) return [];
	return section.split(/\r?\n/).filter((line) => /^(m=video|a=(rtpmap|fmtp|rtcp-fb))/.test(line));
}

/**
 * The range `str0mien1/src/max_video_size.rs` accepts for `max_video_short_side`, from 320x180 up to 8K.
 *
 * Out of range is a deliberate 400 rather than a silent cap, so the publisher finds out — which is worth knowing here,
 * because it means an unvalidated frame size takes the whole session down.
 */
export const MIN_VIDEO_SHORT_SIDE = 180;
export const MAX_VIDEO_SHORT_SIDE = 4320;

/**
 * The short side of what the video track is actually producing, or `null` if it will not say.
 *
 * The transcoder locks its ABR ladder on the first keyframe, before WebRTC has ramped resolution up, so it has to be told
 * the largest frame we can send rather than inferring it from the first one that arrives.
 */
export function videoShortSide(track: MediaStreamTrack): number | null {
	const { width, height } = track.getSettings();
	return width && height ? Math.min(width, height) : null;
}
